// file: src/utils/citationService.js

/**
 * Extracts document ID from document name if it contains an ID pattern
 * @param {string} docName 
 * @returns {string|null} 
 */
function extractDocIdFromName(docName) {
  if (!docName) return null;
  
  // Common pattern: ID might be at the start of the filename before underscore or dash
  const idPattern = /^([a-f0-9]{32})[-_]/i;
  const match = docName.match(idPattern);
  
  return match ? match[1] : null;
}

/**
 * Extracts a clean filename from a full document name
 * @param {string} fullName 
 * @returns {string} 
 */
export function getCleanFileName(fullName) {
  if (!fullName) return 'document';
  
  // Remove any path components
  const filename = fullName.split('/').pop() || fullName;
  
  // If filename starts with an ID pattern (32 hex chars + underscore/dash), remove it
  const cleanedName = filename.replace(/^[a-f0-9]{32}[-_]/i, '');
  
  return cleanedName || filename;
}

/**
 * Normalizes a citation chunk to a standard format
 * @param {Object} raw 
 * @returns {Object|null} 
 */
export function normalizeChunk(raw) {
  if (!raw) return null;
  
  // Validate input type to catch integration bugs early
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    console.warn('[normalizeChunk] Expected object but received:', typeof raw, raw);
    return null;
  }
  
  // Check if this is already a normalized chunk
  if ('isSynthesized' in raw && 'id' in raw && 'page' in raw) {
    return raw;
  }
  
  // Extract content for debugging purposes
  const snippet = raw.snippet || raw.content || raw.content_with_weight || raw.matched_snippet || raw.excerpt || '';
  
  // Try to get document ID from various fields
  let id = raw.id ?? raw.document_id ?? raw.doc_id;
  
  // Extract document name
  const documentName = raw.documentName || raw.document_name || raw.docnm_kwd || 'Unknown Document';
  
  // If no ID but we have a document name, try to extract ID from the name
  if (!id && documentName && documentName !== 'Unknown Document') {
    const extractedId = extractDocIdFromName(documentName);
    if (extractedId) {
      id = extractedId;
    }
  }
  
  // Extract page number from various sources
  let page = raw.page ?? raw.page_number ?? raw.page_num_int;
  
  // If no direct page number, try to get from positions array
  if (!page && raw.positions && Array.isArray(raw.positions) && raw.positions.length > 0) {
    // positions format is typically [[page, x1, y1, x2, y2], ...]
    const firstPosition = raw.positions[0];
    if (Array.isArray(firstPosition) && firstPosition.length > 0) {
      page = firstPosition[0];
    }
  }
  
  // Keep positions array for potential future pixel-perfect features
  const positions = raw.positions || [];
  
  // Check if this is a synthesized result (from KG, aggregated data, etc.)
  const isSynthesized = raw.knowledge_graph_kwd === 'ty2ents' || 
    raw.source_type === 'synthesized' ||
    raw.is_aggregated === true ||
    documentName === 'Related content in Knowledge Graph' || 
    documentName?.toLowerCase().includes('knowledge graph') || 
    ((!id || id === '') && !page && !snippet && !documentName.includes('.'));
  
  if (isSynthesized) {
    const synthesisType = raw.knowledge_graph_kwd === 'ty2ents' || documentName === 'Related content in Knowledge Graph' 
      ? 'knowledge_graph' 
      : 'aggregated';
      
    return {
      isSynthesized: true,
      synthesisType,
      documentName: synthesisType === 'knowledge_graph' ? 'Knowledge Graph' : 'Synthesized Information',
      snippet: snippet || 'Synthesized information from multiple sources',
      id: null,
      page: null,
      positions: [],
      canNavigate: false
    };
  }
  
  // For regular document citations
  const hasNavigableInfo = id && page && Number(page) > 0;
  
  return { 
    id: id ? String(id) : null, 
    page: Number(page) || null, 
    snippet,
    documentName,
    isSynthesized: false,
    positions,
    canNavigate: hasNavigableInfo,
    _raw: {
      doc_id: raw.doc_id,
      document_id: raw.document_id,
      docnm_kwd: raw.docnm_kwd,
      page_num_int: raw.page_num_int
    }
  };
}

/**
 * Gets a citation chunk by index from a reference object
 * @param {Object} reference 
 * @param {number} idx 
 * @returns {Object|null} 
 */
export function chunkByIndex(reference, idx) {
  const chunks = reference?.chunks ?? reference?.reference?.chunks ?? reference?.citations ?? [];
  if (!Array.isArray(chunks) || chunks.length <= idx || idx < 0) {
    return null;
  }
  return normalizeChunk(chunks[idx]);
}

/**
 * Decorates markdown by replacing citation markers with Citation components
 * @param {string} md 
 * @returns {string} 
 */
export function decorateMarkdown(md) {
  if (!md) return '';
  // Replaces ##3$$ with <Citation index="3" />
  return md.replace(/##(\d+)\$\$/g, (_, i) => `<Citation index="${i}" />`);
}

/**
 * Sanitizes internal tags that aren't meant for display
 * @param {string} text 
 * @returns {string} 
 */
export function sanitizeInternalTags(text) {
  if (!text) return '';
  return text.replace(/<think>[\s\S]*?<\/think>/gi, '');
}

/**
 * PDF Navigation utilities
 * Extracted from PDFViewer for reusability across different viewer components
 */
export const refNavigation = {
  /**
   * Calculate current page based on scroll position
   * @param {HTMLElement} container 
   * @param {NodeList} pages 
   * @returns {number} 
   */
  calculateCurrentPage: (container, pages) => {
    if (!container || !pages.length) return 1;
    
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    let closestPage = 1;
    let closestDistance = Infinity;
    
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const pageTop = page.offsetTop - container.offsetTop;
      const pageMiddle = pageTop + page.offsetHeight / 2;
      const viewportMiddle = scrollTop + containerHeight / 2;
      const distance = Math.abs(pageMiddle - viewportMiddle);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = i + 1;
      }
    }
    
    return closestPage;
  },

  /**
   * Scroll to a specific page
   * @param {HTMLElement} container 
   * @param {HTMLElement} targetElement 
   * @param {boolean} instant 
   */
  scrollToPage: (container, targetElement, instant = false) => {
    if (!container || !targetElement) return;
    
    if (instant) {
      container.scrollTop = targetElement.offsetTop - container.offsetTop;
    } else {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  },

  /**
   * Create throttled scroll handler
   * @param {Function} callback 
   * @param {number} delay 
   * @returns {Function}
   */
  createThrottledScrollHandler: (callback, delay = 50) => {
    let rafId = null;
    let lastCallTime = 0;
    
    return () => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;
      
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      if (timeSinceLastCall >= delay) {
        lastCallTime = now;
        callback();
      } else {
        rafId = requestAnimationFrame(() => {
          lastCallTime = Date.now();
          callback();
        });
      }
    };
  },

  /**
   * Format page info for display
   * @param {number} current 
   * @param {number} total 
   * @returns {string} 
   */
  formatPageInfo: (current, total) => {
    return `Page ${current} of ${total}`;
  }
}; 